<template>
  <!--
    A pump, drawn in the water valve's idiom (`switch/SwitchAnimation.vue`): one
    inline SVG on a 48-grid in `currentColor`, the fitting's casing left
    semi-transparent so the water inside it stays visible, and one moving part —
    the impeller — in a colour of its own.

    Off, the pump is dry and still; running, the water fills the casing and runs,
    the impeller turns at the speed the tile names, and each pump's own gesture
    follows (bubbles rising, a sump's float bobbing, a booster's gauge quivering).

    Every kind shares that language so the eight read as one family: `circulator`
    a flanged volute in its riser with the motor bolted to it, `surface` an
    end-suction pump on its base, `submersible` a can inside a flooded well,
    `sump` a pump standing in its pit with the float on its cord, `pool` a
    strainer lid over the body, `well` a slim pump down a borehole under the
    ground line, `sewage` a closed chamber with its check valve and grinder, and
    `booster` a pump beside its pressure vessel.
  -->
  <svg
    class="pump-anim"
    :class="[`pump-anim--${kind}`, { 'pump-anim--active': active }]"
    :style="{ '--pump-spin': spin, '--pump-flow': flow }"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <defs>
      <clipPath :id="chamberClipId">
        <circle cx="24" cy="30.2" r="11.8" />
      </clipPath>
      <clipPath :id="vesselClipId">
        <rect x="32.6" y="10.2" width="9.6" height="30.4" rx="4.8" />
      </clipPath>
    </defs>

    <!-- ── Circulator: the hydronic loop's own pump, bolted into its riser ── -->
    <g v-if="kind === 'circulator'">
      <path class="pump-anim__pipe" d="M24,48 V0" />
      <g class="pump-anim__water">
        <path class="pump-anim__water-line" d="M24,48 V0" />
        <path class="pump-anim__current" d="M24,48 V0" />
      </g>
      <path class="pump-anim__wall" d="M20.4,48 V35.6" />
      <path class="pump-anim__wall" d="M27.6,48 V35.6" />
      <path class="pump-anim__wall" d="M20.4,17.4 V0" />
      <path class="pump-anim__wall" d="M27.6,17.4 V0" />
      <rect class="pump-anim__casing" x="17.8" y="34.6" width="12.4" height="2.8" rx="1.2" />
      <rect class="pump-anim__casing" x="17.8" y="15.6" width="12.4" height="2.8" rx="1.2" />
      <circle class="pump-anim__casing" cx="24" cy="26.4" r="9" />
      <g class="pump-anim__impeller" :style="{ transformOrigin: '24px 26.4px' }">
        <path d="M24,21 V31.8" />
        <path d="M19.2,23.4 L28.8,29.4" />
        <path d="M28.8,23.4 L19.2,29.4" />
      </g>
      <circle class="pump-anim__impeller-hub" cx="24" cy="26.4" r="1.6" />            
    </g>

    <!-- ── Surface: an end-suction pump on its base, suction left, discharge up ── -->
    <g v-else-if="kind === 'surface'">
      <rect class="pump-anim__motor" x="7.4" y="39.4" width="32.6" height="3.4" rx="1.5" />
      <path class="pump-anim__pipe" d="M3,27 H14" />
      <path class="pump-anim__pipe" d="M14.6,24.5 V0" />
      <g class="pump-anim__water">
        <path class="pump-anim__water-line" d="M3,27 H15" />
        <path class="pump-anim__current" d="M3,27 H15" />
        <path class="pump-anim__water-line" d="M14.6,24.5 V0" />
        <path class="pump-anim__current" d="M14.6,24.5 V0" />
      </g>
      <path class="pump-anim__wall" d="M3,23.4 H10.4" />
      <path class="pump-anim__wall" d="M3,30.6 H10.4" />
      <path class="pump-anim__wall" d="M11,20 V0" />
      <path class="pump-anim__wall" d="M18.2,20 V0" />
      <circle class="pump-anim__casing" cx="14.6" cy="27" r="8.4" />
      <path class="pump-anim__seam" d="M11.4,35.4 V39.4" />
      <path class="pump-anim__seam" d="M17.8,35.4 V39.4" />
      <g class="pump-anim__impeller" :style="{ transformOrigin: '14.6px 27px' }">
        <path d="M14.6,21.8 V32.2" />
        <path d="M10,24.4 L19.2,29.6" />
        <path d="M19.2,24.4 L10,29.6" />
      </g>
      <circle class="pump-anim__impeller-hub" cx="14.6" cy="27" r="1.5" />
      <rect class="pump-anim__motor" x="23.4" y="21.2" width="16" height="11.6" rx="2.8" />
      <path class="pump-anim__seam" d="M27.4,23.4 V30.6" />
      <path class="pump-anim__seam" d="M30.8,23.4 V30.6" />
      <path class="pump-anim__seam" d="M34.2,23.4 V30.6" />
      <rect class="pump-anim__accent" x="36.8" y="24.2" width="1.9" height="5.6" rx="0.95" />
    </g>

    <!-- ── Submersible: a can hanging in a flooded well, discharge out the top ── -->
    <g v-else-if="kind === 'submersible'">
      <rect class="pump-anim__casing" x="12.2" y="2.6" width="23.6" height="42.6" rx="3.6" />
      <g class="pump-anim__water">
        <rect class="pump-anim__water-body" x="13.4" y="25.4" width="21.2" height="18.6" />
        <path class="pump-anim__water-surface" d="M13.4,25.4 H34.6" />
        <path class="pump-anim__current" d="M17,42.6 V27.4" />
        <path class="pump-anim__current" d="M31,42.6 V27.4" />
        <path class="pump-anim__water-line" d="M24,12 V0" />
        <path class="pump-anim__current" d="M24,12 V0" />
      </g>
      <path class="pump-anim__pipe" d="M24,12 V0" />
      <path class="pump-anim__wall" d="M20.7,12 V0" />
      <path class="pump-anim__wall" d="M27.3,12 V0" />
      <rect class="pump-anim__motor" x="19.2" y="11.4" width="9.6" height="28" rx="4.6" />
      <path class="pump-anim__seam" d="M19.8,22.4 H28.2" />
      <path class="pump-anim__seam" d="M21.6,34.2 H26.4" />
      <path class="pump-anim__seam" d="M21.6,36.6 H26.4" />
      <path class="pump-anim__seam" d="M21.6,39 H26.4" />
      <g class="pump-anim__impeller" :style="{ transformOrigin: '24px 16.8px' }">
        <path d="M24,14 V19.6" />
        <path d="M21.2,16.8 H26.8" />
      </g>
      <circle class="pump-anim__impeller-hub" cx="24" cy="16.8" r="1.1" />
      <g class="pump-anim__water">
        <circle class="pump-anim__bubble" cx="16" cy="39" r="1.1" />
        <circle class="pump-anim__bubble" cx="32.4" cy="35" r="0.9" />
        <circle class="pump-anim__bubble" cx="15.8" cy="31" r="0.8" />
      </g>
    </g>

    <!-- ── Sump: a pump standing in its own pit, float on its cord ────────── -->
    <g v-else-if="kind === 'sump'">
      <path class="pump-anim__casing" d="M8.2,13.4 V43.4 H39.8 V13.4" />
      <path class="pump-anim__wall" d="M8.2,13.4 H39.8" />
      <g class="pump-anim__water">
        <rect class="pump-anim__water-body" x="9.6" y="29.2" width="28.8" height="13.4" />
        <path class="pump-anim__water-surface" d="M9.6,29.2 H38.4" />
        <path class="pump-anim__current" d="M13.2,42 V31.4" />
        <path class="pump-anim__current" d="M34.8,42 V31.4" />
      </g>
      <path class="pump-anim__pipe" d="M24,31.6 V0" />
      <g class="pump-anim__water">
        <path class="pump-anim__water-line" d="M24,31.6 V0" />
        <path class="pump-anim__current" d="M24,31.6 V0" />
      </g>
      <path class="pump-anim__wall" d="M20.7,13.4 V0" />
      <path class="pump-anim__wall" d="M27.3,13.4 V0" />
      <rect class="pump-anim__motor" x="16.4" y="31.2" width="15.2" height="9.8" rx="2.8" />
      <path class="pump-anim__seam" d="M18.6,39.4 H20.6" />
      <path class="pump-anim__seam" d="M27.4,39.4 H29.4" />
      <g class="pump-anim__impeller" :style="{ transformOrigin: '24px 36px' }">
        <path d="M24,33 V39" />
        <path d="M21,34.5 L27,37.5" />
        <path d="M27,34.5 L21,37.5" />
      </g>
      <circle class="pump-anim__impeller-hub" cx="24" cy="36" r="1.2" />
      <path class="pump-anim__seam" d="M31.2,31.4 L34.2,25.8" />
      <circle class="pump-anim__float" cx="34.9" cy="24.4" r="2.5" />
    </g>

    <!-- ── Pool: the strainer lid over the body, discharge out the top ────── -->
    <g v-else-if="kind === 'pool'">
      <path class="pump-anim__pipe" d="M3,30 H12" />
      <path class="pump-anim__pipe" d="M29.6,24 V0" />
      <g class="pump-anim__water">
        <path class="pump-anim__water-line" d="M3,30 H13" />
        <path class="pump-anim__current" d="M3,30 H13" />
        <path class="pump-anim__water-line" d="M29.6,26 V0" />
        <path class="pump-anim__current" d="M29.6,26 V0" />
      </g>
      <path class="pump-anim__wall" d="M3,26.4 H9.4" />
      <path class="pump-anim__wall" d="M3,33.6 H9.4" />
      <path class="pump-anim__wall" d="M26.3,19.6 V0" />
      <path class="pump-anim__wall" d="M32.9,19.6 V0" />
      <rect class="pump-anim__casing" x="9.6" y="23.6" width="25.4" height="13" rx="4.8" />
      <g class="pump-anim__water">
        <rect class="pump-anim__water-body" x="10.8" y="25.4" width="23" height="9.4" rx="3.6" />
        <path class="pump-anim__current" d="M12.4,30.1 H24.6" />
      </g>
      <circle class="pump-anim__motor" cx="16.6" cy="23.6" r="6.2" />
      <g class="pump-anim__seam">
        <path d="M16.6,18.2 V29" />
        <path d="M11.8,20.6 L21.4,26.6" />
        <path d="M21.4,20.6 L11.8,26.6" />
      </g>
      <g class="pump-anim__impeller" :style="{ transformOrigin: '26.6px 30.1px' }">
        <path d="M26.6,26.4 V33.8" />
        <path d="M22.9,28.2 L30.3,32" />
        <path d="M30.3,28.2 L22.9,32" />
      </g>
      <circle class="pump-anim__impeller-hub" cx="26.6" cy="30.1" r="1.4" />
      <rect class="pump-anim__motor" x="34.6" y="24.9" width="9.4" height="10" rx="2.4" />
      <path class="pump-anim__seam" d="M37.2,26.6 V33.2" />
      <path class="pump-anim__seam" d="M39.6,26.6 V33.2" />
    </g>

    <!-- ── Well: a slim pump down a borehole, under the ground line ───────── -->
    <g v-else-if="kind === 'well'">
      <path class="pump-anim__ground" d="M3.4,11.6 H44.6" />
      <rect class="pump-anim__casing" x="17.4" y="11.6" width="13.2" height="34.6" rx="2.8" />
      <g class="pump-anim__water">
        <rect class="pump-anim__water-body" x="18.6" y="27.2" width="10.8" height="17.8" />
        <path class="pump-anim__water-surface" d="M18.6,27.2 H29.4" />
        <path class="pump-anim__current" d="M21.2,43.8 V29.4" />
        <path class="pump-anim__water-line" d="M24,31 V0" />
        <path class="pump-anim__current" d="M24,31 V0" />
      </g>
      <path class="pump-anim__pipe" d="M24,31 V0" />
      <path class="pump-anim__wall" d="M20.7,11.6 V0" />
      <path class="pump-anim__wall" d="M27.3,11.6 V0" />
      <rect class="pump-anim__motor" x="20.4" y="29.4" width="7.2" height="13.6" rx="3.4" />
      <g class="pump-anim__impeller" :style="{ transformOrigin: '24px 33.6px' }">
        <path d="M24,31.4 V35.8" />
        <path d="M21.8,33.6 H26.2" />
      </g>
      <circle class="pump-anim__impeller-hub" cx="24" cy="33.6" r="1" />
    </g>

    <!-- ── Sewage: a closed chamber, its check valve, and the grinder below ── -->
    <g v-else-if="kind === 'sewage'">
      <circle class="pump-anim__casing" cx="24" cy="30.2" r="11.8" />
      <g class="pump-anim__water" :clip-path="`url(#${chamberClipId})`">
        <rect class="pump-anim__water-body" x="11" y="26.6" width="26" height="17" />
        <path class="pump-anim__water-surface" d="M12.2,26.6 H35.8" />
        <path class="pump-anim__current" d="M17,43 V28.6" />
        <path class="pump-anim__current" d="M31,43 V28.6" />
      </g>
      <path class="pump-anim__pipe" d="M24,30.2 V0" />
      <g class="pump-anim__water">
        <path class="pump-anim__water-line" d="M24,30.2 V0" />
        <path class="pump-anim__current" d="M24,30.2 V0" />
      </g>
      <path class="pump-anim__wall" d="M20.7,30.2 V0" />
      <path class="pump-anim__wall" d="M27.3,30.2 V0" />
      <rect class="pump-anim__motor" x="19.8" y="10.4" width="8.4" height="4.6" rx="1.6" />
      <path class="pump-anim__seam" d="M22,12.7 H26" />
      <circle class="pump-anim__motor" cx="24" cy="37" r="3.8" />
      <g class="pump-anim__impeller" :style="{ transformOrigin: '24px 37px' }">
        <path d="M24,33.8 V40.2" />
        <path d="M21,35.4 L27,38.6" />
        <path d="M27,35.4 L21,38.6" />
      </g>
    </g>

    <!-- ── Booster: a pump beside its pressure vessel, gauge on the vessel ── -->
    <g v-else>
      <rect class="pump-anim__motor" x="6.4" y="40.2" width="35.4" height="3.2" rx="1.5" />
      <path class="pump-anim__pipe" d="M3,30.6 H13.6" />
      <path class="pump-anim__pipe" d="M14.4,22.6 V0" />
      <g class="pump-anim__water">
        <path class="pump-anim__water-line" d="M3,30.6 H14.4" />
        <path class="pump-anim__current" d="M3,30.6 H14.4" />
        <path class="pump-anim__water-line" d="M14.4,22.6 V0" />
        <path class="pump-anim__current" d="M14.4,22.6 V0" />
      </g>
      <path class="pump-anim__wall" d="M3,27 H9" />
      <path class="pump-anim__wall" d="M3,34.2 H9" />
      <path class="pump-anim__wall" d="M11.1,18 V0" />
      <path class="pump-anim__wall" d="M17.7,18 V0" />
      <circle class="pump-anim__casing" cx="14.4" cy="30.6" r="8.2" />
      <path class="pump-anim__seam" d="M11.4,38.6 V40.2" />
      <path class="pump-anim__seam" d="M17.4,38.6 V40.2" />
      <g class="pump-anim__impeller" :style="{ transformOrigin: '14.4px 30.6px' }">
        <path d="M14.4,25.6 V35.6" />
        <path d="M9.9,28.1 L18.9,33.1" />
        <path d="M18.9,28.1 L9.9,33.1" />
      </g>
      <circle class="pump-anim__impeller-hub" cx="14.4" cy="30.6" r="1.5" />
      <rect class="pump-anim__motor" x="21.4" y="26.2" width="9.4" height="9.2" rx="2.4" />
      <path class="pump-anim__seam" d="M24.2,27.8 V33.8" />
      <path class="pump-anim__seam" d="M26.8,27.8 V33.8" />
      <path class="pump-anim__pipe" d="M30.8,34.4 H36.6" />
      <g class="pump-anim__water">
        <path class="pump-anim__water-line" d="M30.8,34.4 H36.6" />
      </g>
      <rect class="pump-anim__casing" x="32.6" y="10.2" width="9.6" height="30.4" rx="4.8" />
      <g class="pump-anim__water" :clip-path="`url(#${vesselClipId})`">
        <rect class="pump-anim__water-body" x="33.6" y="24" width="7.6" height="15.6" />
        <path class="pump-anim__water-surface" d="M33.6,24 H41.2" />
      </g>
      <circle class="pump-anim__gauge" cx="37.4" cy="16.4" r="2.8" />
      <path class="pump-anim__needle" d="M37.4,16.4 L39,14.6" />
    </g>
  </svg>
</template>

<script lang="ts">
/** Module scope: the clip ids must be unique per instance. */
let instanceSeq = 0;
</script>

<script setup lang="ts">
import { computed } from 'vue';
import type { PumpKind } from '../deviceWidgetTypes';

const props = withDefaults(
  defineProps<{
    kind?: PumpKind;
    /** The pump is running — the only thing the water and the movement key off. */
    active?: boolean;
    /** How hard it runs, 0–100 (a stepped speed's reached step, or a % speed). */
    speed?: number | null;
  }>(),
  { kind: 'circulator', active: false, speed: null },
);

const uid = `pump-anim-${++instanceSeq}`;
const chamberClipId = `${uid}-chamber`;
const vesselClipId = `${uid}-vessel`;

/** A plain on/off pump still spins with authority; a speed turns it further up. */
const ratio = computed(() => {
  if (props.speed === null || props.speed === undefined) return 0.6;
  const value = Number(props.speed);
  if (!Number.isFinite(value)) return 0.6;
  return Math.min(1, Math.max(0, value / 100));
});

/** The impeller's turn — quicker the harder the pump works, never a blur. */
const spin = computed(() => `${(2.8 - 1.8 * ratio.value).toFixed(2)}s`);

/** The current in the water, one dash-period per turn of its own. */
const flow = computed(() => `${(2.4 - 1.3 * ratio.value).toFixed(2)}s`);
</script>

<style scoped>
.pump-anim {
  display: block;
  width: 100%;
  height: 100%;
}

/* ── The casing: metal, and semi-transparent so the water shows through it ── */
.pump-anim__casing {
  fill: color-mix(in srgb, currentColor 30%, transparent);
  stroke: color-mix(in srgb, currentColor 78%, rgb(0 0 0));
  stroke-width: 1.2;
}

.pump-anim__motor {
  fill: color-mix(in srgb, currentColor 52%, transparent);
  stroke: color-mix(in srgb, currentColor 80%, rgb(0 0 0));
  stroke-width: 1.1;
}

.pump-anim__pipe {
  fill: none;
  stroke: color-mix(in srgb, currentColor 34%, transparent);
  stroke-width: 6.6;
}

.pump-anim__wall {
  fill: none;
  stroke: color-mix(in srgb, currentColor 74%, transparent);
  stroke-width: 1.3;
  stroke-linecap: round;
}

/* The ground the borehole is drilled through */
.pump-anim__ground {
  fill: none;
  stroke: color-mix(in srgb, currentColor 60%, transparent);
  stroke-width: 1.6;
  stroke-linecap: round;
}

.pump-anim__seam {
  fill: none;
  stroke: color-mix(in srgb, currentColor 72%, transparent);
  stroke-width: 0.9;
  stroke-linecap: round;
}

/* ── Water: a blue body, its surface, and the lighter current running in it ── */
.pump-anim__water {
  opacity: 0;
  transition: opacity 0.4s ease;
}

.pump-anim--active .pump-anim__water {
  opacity: 1;
}

.pump-anim__water-body {
  fill: #38bdf8;
  fill-opacity: 0.55;
}

.pump-anim__water-line {
  fill: none;
  stroke: #38bdf8;
  stroke-width: 4.2;
  stroke-linecap: round;
}

.pump-anim__water-surface {
  fill: none;
  stroke: #7dd3fc;
  stroke-width: 1.1;
  stroke-linecap: round;
}

.pump-anim__current {
  fill: none;
  stroke: #e0f2fe;
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-dasharray: 3 6;
}

.pump-anim__bubble {
  fill: #7dd3fc;
}

/* ── The part that turns, in a colour of its own — the valve's lever, the
      meter's hand: one coloured gesture says "this machine is working" ─────── */
.pump-anim__impeller {
  fill: none;
  stroke: #f59e0b;
  stroke-width: 1.6;
  stroke-linecap: round;
}

.pump-anim__impeller-hub {
  fill: #f59e0b;
}

.pump-anim__accent {
  fill: #f59e0b;
}

.pump-anim__float {
  fill: #f59e0b;
  stroke: #b45309;
  stroke-width: 0.8;
}

.pump-anim__gauge {
  fill: #f8fafc;
  stroke: color-mix(in srgb, currentColor 70%, rgb(0 0 0));
  stroke-width: 0.9;
}

.pump-anim__needle {
  fill: none;
  stroke: #dc2626;
  stroke-width: 1.2;
  stroke-linecap: round;
}

.pump-anim--booster .pump-anim__needle {
  transform-box: view-box;
  transform-origin: 37.4px 16.4px;
}

/* ── Running: the impeller turns, the water runs, and each pump's own gesture ── */
.pump-anim--active .pump-anim__impeller {
  animation: pump-anim-spin var(--pump-spin, 1.6s) linear infinite;
}

.pump-anim--active .pump-anim__current {
  animation: pump-anim-current var(--pump-flow, 1.2s) linear infinite;
}

.pump-anim--active .pump-anim__bubble {
  animation: pump-anim-bubble 1.9s ease-in infinite;
}

.pump-anim--active .pump-anim__bubble:nth-child(2) {
  animation-delay: 0.6s;
}

.pump-anim--active .pump-anim__bubble:nth-child(3) {
  animation-delay: 1.1s;
}

.pump-anim--active .pump-anim__float {
  animation: pump-anim-bob 2.6s ease-in-out infinite;
}

.pump-anim--active .pump-anim__needle {
  animation: pump-anim-quiver 2.2s ease-in-out infinite;
}

@keyframes pump-anim-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pump-anim-current {
  to {
    stroke-dashoffset: -9;
  }
}

@keyframes pump-anim-bubble {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  25% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translateY(-9px);
  }
}

@keyframes pump-anim-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(2.2px);
  }
}

@keyframes pump-anim-quiver {
  0%,
  100% {
    transform: rotate(-14deg);
  }
  50% {
    transform: rotate(12deg);
  }
}

/* Reduced motion: the drawing stays, the movement stops. */
@media (prefers-reduced-motion: reduce) {
  .pump-anim--active .pump-anim__impeller,
  .pump-anim--active .pump-anim__current,
  .pump-anim--active .pump-anim__bubble,
  .pump-anim--active .pump-anim__float,
  .pump-anim--active .pump-anim__needle {
    animation: none !important;
  }
}
</style>
